/**
 * Merge live-card elements with stored confirmations / pricingSelections.
 */

import {
  filterCustomerIntakeElements,
  isAccessChecklistElement,
} from "./customerIntakeElements.js";

export const CONFIRMATION_STATUSES = Object.freeze([
  "present",
  "absent",
  "unknown",
]);

/**
 * @param {unknown} status
 * @returns {"present"|"absent"|"unknown"}
 */
export function normalizeStatus(status) {
  const raw = String(status || "")
    .trim()
    .toLowerCase();
  if (raw === "present" || raw === "absent") return raw;
  return "unknown";
}

/**
 * @param {unknown} list
 * @returns {Array<{ id: string, status: string, value?: unknown }>}
 */
export function normalizeConfirmations(list) {
  if (!Array.isArray(list)) return [];
  const out = [];
  const seen = new Set();
  for (const row of list) {
    if (!row || typeof row !== "object") continue;
    const id = String(row.id || "").trim();
    if (!id || seen.has(id)) continue;
    seen.add(id);
    const entry = { id, status: normalizeStatus(row.status) };
    if (row.value !== undefined) entry.value = row.value;
    out.push(entry);
  }
  return out;
}

/**
 * @param {{
 *   elements?: object[],
 *   confirmations?: unknown,
 *   pricingSelections?: unknown,
 * }} input
 * @returns {{
 *   accessRows: object[],
 *   addonRows: object[],
 *   card: object|null,
 *   incompleteAccess: boolean,
 *   unknownAddons: boolean,
 * }}
 */
export function buildElementChecklistRows({
  elements = [],
  confirmations,
  pricingSelections,
} = {}) {
  const visible = filterCustomerIntakeElements(elements);
  const confirmationById = new Map(
    normalizeConfirmations(confirmations).map((row) => [row.id, row]),
  );
  const presentFromSelections = new Set(
    (Array.isArray(pricingSelections) ? pricingSelections : [])
      .map((row) => String(row?.id || "").trim())
      .filter(Boolean),
  );
  const selectionValueById = new Map(
    (Array.isArray(pricingSelections) ? pricingSelections : [])
      .filter((row) => row?.id)
      .map((row) => [
        String(row.id).trim(),
        row.value !== undefined
          ? row.value
          : row.quantity !== undefined
            ? row.quantity
            : true,
      ]),
  );

  const rows = visible.map((element) => {
    const id = String(element.id);
    const stored = confirmationById.get(id);
    let status = "unknown";
    let value;
    if (stored) {
      status = stored.status;
      value = stored.value;
    } else if (presentFromSelections.has(id)) {
      status = "present";
      value = selectionValueById.get(id);
    }
    return {
      id,
      name: element.name || element.label || id,
      type: element.type,
      group: element.group,
      control: element.control || "toggle",
      unit: element.unit || "",
      options: element.options || [],
      min: element.min,
      max: element.max,
      step: element.step,
      isAccess: isAccessChecklistElement(element),
      status,
      value,
    };
  });

  const accessRows = rows.filter((row) => row.isAccess);
  const addonRows = rows.filter((row) => !row.isAccess);

  return {
    accessRows,
    addonRows,
    card: null,
    incompleteAccess: accessRows.some((row) => row.status === "unknown"),
    unknownAddons: addonRows.some((row) => row.status === "unknown"),
  };
}

/**
 * @param {Array<{ id: string, status: string, value?: unknown }>} rows
 * @returns {Array<{ id: string, status: string, value?: unknown }>}
 */
export function rowsToConfirmations(rows = []) {
  return (Array.isArray(rows) ? rows : [])
    .filter((row) => row?.id)
    .map((row) => {
      const entry = {
        id: String(row.id),
        status: normalizeStatus(row.status),
      };
      if (entry.status === "present" && row.value !== undefined) {
        entry.value = row.value;
      }
      return entry;
    });
}

/**
 * Seed confirmations for every visible element.
 * Prefer existing confirmations; else infer present from pricingSelections;
 * remaining rows stay unknown (never invent absent).
 * @param {object[]} elements
 * @param {unknown} existing
 * @param {unknown} [pricingSelections]
 */
export function seedUnknownConfirmations(
  elements = [],
  existing,
  pricingSelections,
) {
  const prior = new Map(
    normalizeConfirmations(existing).map((row) => [row.id, row]),
  );
  const presentFromSelections = new Map(
    (Array.isArray(pricingSelections) ? pricingSelections : [])
      .filter((row) => row?.id)
      .map((row) => [
        String(row.id).trim(),
        row.value !== undefined
          ? row.value
          : row.quantity !== undefined
            ? row.quantity
            : true,
      ]),
  );
  return filterCustomerIntakeElements(elements).map((el) => {
    const id = String(el.id);
    if (prior.has(id)) return prior.get(id);
    if (presentFromSelections.has(id)) {
      return {
        id,
        status: "present",
        value: presentFromSelections.get(id),
      };
    }
    return { id, status: "unknown" };
  });
}

/**
 * Build editable confirmation list from lead + manifest (Screens B/C).
 */
export function seedConfirmationsForLeadEdit({
  elements = [],
  confirmations,
  pricingSelections,
} = {}) {
  return seedUnknownConfirmations(elements, confirmations, pricingSelections);
}

/**
 * Update one confirmation in a list.
 */
export function upsertConfirmation(list, { id, status, value }) {
  const normalized = normalizeConfirmations(list);
  const nextStatus = normalizeStatus(status);
  const idx = normalized.findIndex((row) => row.id === id);
  const entry = { id, status: nextStatus };
  if (nextStatus === "present" && value !== undefined) entry.value = value;
  if (idx >= 0) {
    const copy = [...normalized];
    copy[idx] = entry;
    return copy;
  }
  return [...normalized, entry];
}

/**
 * Mark all non-access confirmations absent.
 */
export function markAllAddonsAbsent(list, elements = []) {
  const addonIds = new Set(
    filterCustomerIntakeElements(elements)
      .filter((el) => !isAccessChecklistElement(el))
      .map((el) => String(el.id)),
  );
  const byId = new Map(
    normalizeConfirmations(list).map((row) => [row.id, row]),
  );
  for (const id of addonIds) {
    byId.set(id, { id, status: "absent" });
  }
  return Array.from(byId.values());
}
